// assets/byte-slideshow.js
// Slideshow behavior for sections/slideshow.liquid.
// - Image slides advance on the "Seconds between slides" timer.
// - Video slides wait until the video has fully played, then advance.
// - Click-to-play videos: once the visitor starts the video, the slideshow
//   waits for it to finish (or for manual navigation).

(() => {
  if (window.ByteSlideshow) return;

  const MOBILE_MEDIA_QUERY = window.matchMedia("(max-width: 767px)");
  const DOT_WINDOW_SIZE = 5;
  const SWIPE_DISTANCE = 50;
  const DRAG_THRESHOLD = 6;
  const CONTROLS_IDLE_DELAY = 2500;
  const DRAG_IGNORE_SELECTOR =
    "button, input, select, textarea, label, [data-slideshow-no-drag]";

  class ByteSlideshow {
    #abortController = new AbortController();
    #blockedVideos = new WeakSet();
    #timerId = null;
    #timerStartedAt = 0;
    #timerRemaining = 0;

    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll("[data-slideshow-slide]"));
      this.dots = Array.from(root.querySelectorAll("[data-slideshow-dot]"));
      this.dotsWrapper = root.querySelector("[data-slideshow-dots]");
      this.prevButton = root.querySelector("[data-slideshow-prev]");
      this.nextButton = root.querySelector("[data-slideshow-next]");
      this.totalSlides = this.slides.length;
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains("is-active")),
      );
      this.hasAutoplay =
        root.dataset.autoplay === "true" && this.totalSlides > 1;
      this.autoplayDelay =
        (parseInt(root.dataset.autoplaySpeed, 10) || 5) * 1000;
      this.isHovered = false;
      this.isEditorPinned = false;
      this.isVideoEngaged = false;
      this.hasPendingAdvance = false;

      if (this.totalSlides === 0) return;

      this.#prepareVideos();
      this.#bindPlayButtons();
      this.#bindBreakpointChange();

      if (this.totalSlides < 2) {
        this.#activateSlideMedia(this.currentIndex);
        return;
      }

      this.root.classList.add("can-drag");

      if (this.dotsWrapper && this.dots.length > DOT_WINDOW_SIZE) {
        this.dotsWrapper.classList.add("ss-dots--windowed");
      }

      this.#bindNavigation();
      this.#bindHover();
      this.#bindTouch();
      this.#bindMouseDrag();
      this.#updateDotWindow();
      this.#activateSlideMedia(this.currentIndex);
      this.#scheduleAdvance();
    }

    handlePointerEnter(event) {
      if (event.pointerType !== "mouse") return;

      this.isHovered = true;

      // Image slides pause on hover. Playing videos keep playing.
      if (this.#timerId === null) return;

      this.#pauseTimer();
      this.#setProgressPaused(true);
    }

    handlePointerLeave(event) {
      if (event.pointerType !== "mouse") return;

      this.isHovered = false;

      if (this.hasPendingAdvance) {
        this.hasPendingAdvance = false;
        this.next();
        return;
      }

      if (!this.hasAutoplay || this.isEditorPinned) return;
      if (this.#timerId !== null) return;
      if (this.#getAdvanceVideo(this.#getCurrentSlide())) return;

      this.#setProgressPaused(false);
      this.#startTimer();
    }

    handleBreakpointChange() {
      const currentSlide = this.#getCurrentSlide();
      if (currentSlide.dataset.slideType !== "video") return;

      const slideVideos = this.#getSlideVideos(currentSlide);
      if (slideVideos.length < 2) return;

      const activeVideo = this.#getActiveVideo(currentSlide);
      const wasPlaying = slideVideos.some(
        (video) => video !== activeVideo && !video.paused && !video.ended,
      );

      for (const video of slideVideos) {
        if (video !== activeVideo) video.pause();
      }

      if (!activeVideo) return;

      const isAutoplayVideo =
        currentSlide.dataset.slideVideoAutoplay === "true";
      if (wasPlaying || isAutoplayVideo) this.#playVideo(activeVideo);

      this.#scheduleAdvance();
    }

    goTo(targetIndex) {
      const nextIndex = (targetIndex + this.totalSlides) % this.totalSlides;
      if (nextIndex === this.currentIndex) return;

      this.#clearTimer();
      this.hasPendingAdvance = false;
      this.isVideoEngaged = false;

      this.#setSlideState(this.currentIndex, false);
      this.#deactivateSlideMedia(this.currentIndex);

      this.currentIndex = nextIndex;

      this.#setSlideState(this.currentIndex, true);
      this.#updateDotWindow();
      this.#activateSlideMedia(this.currentIndex);
      this.#scheduleAdvance();
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    prev() {
      this.goTo(this.currentIndex - 1);
    }

    pin(slideIndex) {
      this.isEditorPinned = true;
      if (Number.isInteger(slideIndex)) this.goTo(slideIndex);
      this.#clearTimer();
      this.#setProgressPaused(true);
    }

    unpin() {
      this.isEditorPinned = false;
      this.hasPendingAdvance = false;
      this.#scheduleAdvance();
    }

    destroy() {
      this.#clearTimer();
      this.#abortController.abort();
    }

    // ---------------------------------------------------------------------
    // Setup
    // ---------------------------------------------------------------------

    #prepareVideos() {
      const { signal } = this.#abortController;

      for (const [slideIndex, slide] of this.slides.entries()) {
        for (const video of this.#getSlideVideos(slide)) {
          // With slideshow autoplay on, the slide advances when the video
          // ends, so the video itself must not loop (loop never fires "ended").
          if (this.hasAutoplay) video.loop = false;

          // Only the active slide may play. Hidden slides start paused so
          // they play from the beginning when they become active.
          if (slideIndex !== this.currentIndex) {
            video.autoplay = false;
            video.pause();
          }

          video.addEventListener("play", () => this.#handleVideoPlay(video), {
            signal,
          });
          video.addEventListener(
            "playing",
            () => this.#handleVideoProgressSync(video),
            { signal },
          );
          video.addEventListener(
            "loadedmetadata",
            () => this.#handleVideoProgressSync(video),
            { signal },
          );
          video.addEventListener(
            "waiting",
            () => this.#handleVideoPause(video),
            { signal },
          );
          video.addEventListener("pause", () => this.#handleVideoPause(video), {
            signal,
          });
          video.addEventListener("ended", () => this.#handleVideoEnded(video), {
            signal,
          });
          video.addEventListener("error", () => this.#handleVideoError(video), {
            signal,
          });
        }
      }
    }

    #bindNavigation() {
      const { signal } = this.#abortController;

      this.prevButton?.addEventListener("click", () => this.prev(), { signal });
      this.nextButton?.addEventListener("click", () => this.next(), { signal });

      for (const dot of this.dots) {
        dot.addEventListener(
          "click",
          () => this.goTo(parseInt(dot.dataset.slideshowDot, 10)),
          { signal },
        );
      }
    }

    #bindHover() {
      const { signal } = this.#abortController;

      // Pointer events with a mouse check: touch taps fire emulated
      // mouseenter without mouseleave, which used to freeze autoplay on phones.
      this.root.addEventListener(
        "pointerenter",
        (event) => this.handlePointerEnter(event),
        { signal },
      );
      this.root.addEventListener(
        "pointerleave",
        (event) => this.handlePointerLeave(event),
        { signal },
      );
    }

    #bindBreakpointChange() {
      const { signal } = this.#abortController;

      MOBILE_MEDIA_QUERY.addEventListener(
        "change",
        () => this.handleBreakpointChange(),
        { signal },
      );
    }

    #bindTouch() {
      const { signal } = this.#abortController;
      let touchStartX = 0;

      this.root.addEventListener(
        "touchstart",
        (event) => {
          touchStartX = event.changedTouches[0].screenX;
        },
        { passive: true, signal },
      );

      this.root.addEventListener(
        "touchend",
        (event) => {
          const swipeDistance = touchStartX - event.changedTouches[0].screenX;
          if (Math.abs(swipeDistance) <= SWIPE_DISTANCE) return;

          if (swipeDistance > 0) {
            this.next();
          } else {
            this.prev();
          }
        },
        { passive: true, signal },
      );
    }

    #bindMouseDrag() {
      const { signal } = this.#abortController;

      let activePointerId = null;
      let dragStartX = 0;
      let isDragging = false;
      let shouldBlockClick = false;

      const handlePointerDown = (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        if (event.target.closest(DRAG_IGNORE_SELECTOR)) return;

        activePointerId = event.pointerId;
        dragStartX = event.clientX;
        isDragging = false;
        shouldBlockClick = false;
      };

      const handlePointerMove = (event) => {
        if (event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - dragStartX;

        if (!isDragging) {
          if (Math.abs(deltaX) < DRAG_THRESHOLD) return;

          isDragging = true;
          shouldBlockClick = true;
          this.root.classList.add("is-dragging");
          this.root.setPointerCapture(activePointerId);
          window.getSelection()?.removeAllRanges();
        }

        event.preventDefault();
      };

      const handlePointerEnd = (event) => {
        if (event.pointerId !== activePointerId) return;

        activePointerId = null;
        if (!isDragging) return;

        isDragging = false;
        this.root.classList.remove("is-dragging");

        if (this.root.hasPointerCapture(event.pointerId)) {
          this.root.releasePointerCapture(event.pointerId);
        }

        if (event.type === "pointercancel") return;

        const swipeDistance = dragStartX - event.clientX;
        if (Math.abs(swipeDistance) < SWIPE_DISTANCE) return;

        if (swipeDistance > 0) {
          this.next();
        } else {
          this.prev();
        }
      };

      const handleClickCapture = (event) => {
        if (!shouldBlockClick) return;

        event.preventDefault();
        event.stopPropagation();
        shouldBlockClick = false;
      };

      const handleDragStart = (event) => {
        event.preventDefault();
      };

      this.root.addEventListener("pointerdown", handlePointerDown, { signal });
      this.root.addEventListener("pointermove", handlePointerMove, { signal });
      this.root.addEventListener("pointerup", handlePointerEnd, { signal });
      this.root.addEventListener("pointercancel", handlePointerEnd, { signal });
      this.root.addEventListener("click", handleClickCapture, {
        capture: true,
        signal,
      });
      this.root.addEventListener("dragstart", handleDragStart, { signal });
    }

    #bindPlayButtons() {
      for (const playButton of this.root.querySelectorAll(
        "[data-slide-play]",
      )) {
        const slide = playButton.closest("[data-slideshow-slide]");
        if (slide) this.#bindPlayButton(playButton, slide);
      }
    }

    #bindPlayButton(playButton, slide) {
      const { signal } = this.#abortController;
      let idleTimerId = null;

      const showControls = () => {
        slide.classList.remove("is-controls-idle");
        clearTimeout(idleTimerId);

        if (!slide.classList.contains("is-playing")) return;

        idleTimerId = setTimeout(
          () => slide.classList.add("is-controls-idle"),
          CONTROLS_IDLE_DELAY,
        );
      };

      const syncPlayState = () => {
        const activeVideo = this.#getActiveVideo(slide);
        const isPlaying =
          Boolean(activeVideo) && !activeVideo.paused && !activeVideo.ended;

        slide.classList.toggle("is-playing", isPlaying);
        playButton.setAttribute("aria-pressed", String(isPlaying));

        if (isPlaying) {
          showControls();
          return;
        }

        slide.classList.remove("is-controls-idle");
        clearTimeout(idleTimerId);
      };

      playButton.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          const activeVideo = this.#getActiveVideo(slide);
          if (!activeVideo) return;

          if (activeVideo.paused || activeVideo.ended) {
            this.#playVideo(activeVideo);
          } else {
            activeVideo.pause();
          }
        },
        { signal },
      );

      slide.addEventListener("mousemove", showControls, { signal });
      slide.addEventListener("touchstart", showControls, {
        passive: true,
        signal,
      });
      slide.addEventListener(
        "mouseleave",
        () => {
          if (!slide.classList.contains("is-playing")) return;

          clearTimeout(idleTimerId);
          slide.classList.add("is-controls-idle");
        },
        { signal },
      );

      for (const video of this.#getSlideVideos(slide)) {
        video.addEventListener("play", syncPlayState, { signal });
        video.addEventListener("pause", syncPlayState, { signal });
        video.addEventListener("ended", syncPlayState, { signal });
      }

      signal.addEventListener("abort", () => clearTimeout(idleTimerId));
    }

    // ---------------------------------------------------------------------
    // Video event handling
    // ---------------------------------------------------------------------

    #handleVideoPlay(video) {
      if (!this.#isCurrentVideo(video)) return;

      // Once a video on the active slide starts, the slideshow waits for it.
      this.isVideoEngaged = true;
      this.#scheduleAdvance();
    }

    #handleVideoProgressSync(video) {
      if (!this.hasAutoplay || this.isEditorPinned) return;
      if (!this.#isCurrentVideo(video)) return;
      if (this.#getAdvanceVideo(this.#getCurrentSlide()) !== video) return;

      this.#syncProgressToVideo(video);
    }

    #handleVideoPause(video) {
      if (video.ended) return;
      if (!this.#isCurrentVideo(video)) return;
      if (this.#timerId !== null) return;

      this.#setProgressPaused(true);
    }

    #handleVideoEnded(video) {
      if (!this.hasAutoplay) return;
      if (!this.#isCurrentVideo(video)) return;

      if (this.isHovered || this.isEditorPinned) {
        this.hasPendingAdvance = !this.isEditorPinned;
        return;
      }

      this.next();
    }

    #handleVideoError(video) {
      this.#blockedVideos.add(video);

      if (this.#isCurrentVideo(video)) this.#scheduleAdvance();
    }

    // ---------------------------------------------------------------------
    // Autoplay timing
    // ---------------------------------------------------------------------

    #scheduleAdvance() {
      this.#clearTimer();

      if (!this.hasAutoplay || this.isEditorPinned) return;

      const advanceVideo = this.#getAdvanceVideo(this.#getCurrentSlide());

      // Video slide: no timer. The "ended" event moves to the next slide.
      if (advanceVideo) {
        this.#syncProgressToVideo(advanceVideo);
        return;
      }

      this.#timerRemaining = this.autoplayDelay;
      this.#startProgress(this.autoplayDelay, 0);

      if (this.isHovered) {
        this.#setProgressPaused(true);
        return;
      }

      this.#startTimer();
    }

    #startTimer() {
      this.#clearTimer();
      this.#timerStartedAt = performance.now();
      this.#timerId = window.setTimeout(() => {
        this.#timerId = null;
        this.next();
      }, this.#timerRemaining);
    }

    #pauseTimer() {
      if (this.#timerId === null) return;

      const elapsedTime = performance.now() - this.#timerStartedAt;
      this.#timerRemaining = Math.max(0, this.#timerRemaining - elapsedTime);
      this.#clearTimer();
    }

    #clearTimer() {
      if (this.#timerId === null) return;

      clearTimeout(this.#timerId);
      this.#timerId = null;
    }

    // ---------------------------------------------------------------------
    // Media helpers
    // ---------------------------------------------------------------------

    #getCurrentSlide() {
      return this.slides[this.currentIndex];
    }

    #getSlideVideos(slide) {
      return Array.from(slide.querySelectorAll("video"));
    }

    // Shopify's video_tag only exposes the class attribute here, so the
    // desktop/mobile split follows the classes rendered by the section.
    #getActiveVideo(slide) {
      const mobileVideo = slide.querySelector(".ss-slide__video--mobile");
      const desktopVideo = slide.querySelector(".ss-slide__video--desktop");

      if (MOBILE_MEDIA_QUERY.matches && mobileVideo) return mobileVideo;

      return desktopVideo || mobileVideo;
    }

    #isCurrentVideo(video) {
      return this.#getActiveVideo(this.#getCurrentSlide()) === video;
    }

    // Returns the video the slideshow should wait for, or null when the
    // regular timer applies (image slide, poster-only slide, blocked video,
    // or a click-to-play video the visitor has not started).
    #getAdvanceVideo(slide) {
      if (slide.dataset.slideType !== "video") return null;

      const activeVideo = this.#getActiveVideo(slide);
      if (!activeVideo || activeVideo.error) return null;
      if (this.isVideoEngaged) return activeVideo;

      const isAutoplayVideo = slide.dataset.slideVideoAutoplay === "true";
      if (isAutoplayVideo && !this.#blockedVideos.has(activeVideo))
        return activeVideo;

      return null;
    }

    #playVideo(video) {
      const playPromise = video.play();
      if (!playPromise) return;

      playPromise.catch((error) => {
        // Autoplay blocked (e.g. iOS Low Power Mode): fall back to the timer.
        if (error?.name !== "NotAllowedError") return;

        this.#blockedVideos.add(video);
        if (this.#isCurrentVideo(video)) this.#scheduleAdvance();
      });
    }

    #activateSlideMedia(slideIndex) {
      const slide = this.slides[slideIndex];
      if (slide.dataset.slideType !== "video") return;

      const activeVideo = this.#getActiveVideo(slide);

      for (const video of this.#getSlideVideos(slide)) {
        if (video !== activeVideo) video.pause();
      }

      if (!activeVideo) return;
      if (slide.dataset.slideVideoAutoplay !== "true") return;

      // Browsers only allow muted autoplay.
      activeVideo.muted = true;
      this.#playVideo(activeVideo);
    }

    #deactivateSlideMedia(slideIndex) {
      const slide = this.slides[slideIndex];

      for (const video of this.#getSlideVideos(slide)) {
        video.pause();
        video.currentTime = 0;
      }

      slide.classList.remove("is-playing", "is-controls-idle");
    }

    // ---------------------------------------------------------------------
    // Render / state updates
    // ---------------------------------------------------------------------

    #setSlideState(slideIndex, isActive) {
      const slide = this.slides[slideIndex];
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));

      const dot = this.dots[slideIndex];
      if (!dot) return;

      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-selected", String(isActive));

      if (!isActive) this.#resetProgress(dot);
    }

    #syncProgressToVideo(video) {
      const videoDuration = video.duration;
      if (!Number.isFinite(videoDuration) || videoDuration <= 0) return;

      this.#startProgress(videoDuration * 1000, video.currentTime * 1000);
      this.#setProgressPaused(video.paused);
    }

    // Restarts the active dot's fill animation. A negative delay lets the
    // fill start part-way through, so it stays in sync with the video.
    #startProgress(durationMs, elapsedMs) {
      const activeDot = this.dots[this.currentIndex];
      if (!activeDot) return;

      const progressFill = activeDot.querySelector("[data-slideshow-dot-fill]");
      if (!progressFill) return;

      activeDot.classList.remove("has-progress", "is-progress-paused");
      progressFill.style.animationDuration = `${Math.round(durationMs)}ms`;
      progressFill.style.animationDelay = `-${Math.round(elapsedMs)}ms`;
      void progressFill.offsetWidth;
      activeDot.classList.add("has-progress");
    }

    #setProgressPaused(isPaused) {
      const activeDot = this.dots[this.currentIndex];
      if (!activeDot || !activeDot.classList.contains("has-progress")) return;

      activeDot.classList.toggle("is-progress-paused", isPaused);
    }

    #resetProgress(dot) {
      dot.classList.remove("has-progress", "is-progress-paused");

      const progressFill = dot.querySelector("[data-slideshow-dot-fill]");
      if (!progressFill) return;

      progressFill.style.animationDuration = "";
      progressFill.style.animationDelay = "";
    }

    #updateDotWindow() {
      if (this.dots.length <= DOT_WINDOW_SIZE) return;

      const halfWindow = Math.floor(DOT_WINDOW_SIZE / 2);
      let windowStart = Math.max(0, this.currentIndex - halfWindow);
      let windowEnd = windowStart + DOT_WINDOW_SIZE - 1;

      if (windowEnd > this.totalSlides - 1) {
        windowEnd = this.totalSlides - 1;
        windowStart = windowEnd - DOT_WINDOW_SIZE + 1;
      }

      const hasMoreLeft = windowStart > 0;
      const hasMoreRight = windowEnd < this.totalSlides - 1;

      for (const [dotIndex, dot] of this.dots.entries()) {
        const isInWindow = dotIndex >= windowStart && dotIndex <= windowEnd;
        const isLeftEdge =
          isInWindow && dotIndex === windowStart && hasMoreLeft;
        const isRightEdge =
          isInWindow && dotIndex === windowEnd && hasMoreRight;

        dot.classList.toggle("is-in-window", isInWindow);
        dot.classList.toggle("is-edge", isLeftEdge || isRightEdge);
      }
    }
  }

  window.ByteSlideshow = ByteSlideshow;

  const slideshowInstances = new WeakMap();

  function initSlideshows(scope = document) {
    for (const slideshowRoot of scope.querySelectorAll("[data-slideshow]")) {
      if (slideshowInstances.has(slideshowRoot)) continue;

      slideshowInstances.set(slideshowRoot, new ByteSlideshow(slideshowRoot));
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initSlideshows());
  } else {
    initSlideshows();
  }

  // Theme editor lifecycle
  document.addEventListener("shopify:section:load", (event) => {
    initSlideshows(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    for (const slideshowRoot of event.target.querySelectorAll(
      "[data-slideshow]",
    )) {
      slideshowInstances.get(slideshowRoot)?.destroy();
      slideshowInstances.delete(slideshowRoot);
    }
  });

  document.addEventListener("shopify:block:select", (event) => {
    const slideshowRoot = event.target.closest("[data-slideshow]");
    const slideshow = slideshowRoot && slideshowInstances.get(slideshowRoot);
    if (!slideshow) return;

    slideshow.pin(parseInt(event.target.dataset.slideIndex, 10));
  });

  document.addEventListener("shopify:block:deselect", (event) => {
    const slideshowRoot = event.target.closest("[data-slideshow]");
    slideshowInstances.get(slideshowRoot)?.unpin();
  });
})();
